import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiHelpers } from '@/lib/api';
import {
  ArrowLeft,
  BarChart3,
  Users,
  Eye,
  TrendingUp,
  TrendingDown,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  Loader2,
  Activity,
  MousePointer,
} from 'lucide-react';

const PERIOD_OPTIONS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

export default function Analytics() {
  const { projectId } = useParams();
  const [period, setPeriod] = useState('30d');

  // Fetch analytics data
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['analytics', 'summary', projectId, period],
    queryFn: () => apiHelpers.getAnalyticsSummary(projectId!, period),
    enabled: Boolean(projectId),
  });

  const { data: topPagesData, isLoading: pagesLoading } = useQuery({
    queryKey: ['analytics', 'top-pages', projectId, period],
    queryFn: () => apiHelpers.getAnalyticsTopPages(projectId!, period, 5),
    enabled: Boolean(projectId) && summaryData?.data?.data?.enabled,
  });

  const { data: sourcesData, isLoading: sourcesLoading } = useQuery({
    queryKey: ['analytics', 'sources', projectId, period],
    queryFn: () => apiHelpers.getAnalyticsSources(projectId!, period, 5),
    enabled: Boolean(projectId) && summaryData?.data?.data?.enabled,
  });

  const { data: devicesData, isLoading: devicesLoading } = useQuery({
    queryKey: ['analytics', 'devices', projectId, period],
    queryFn: () => apiHelpers.getAnalyticsDevices(projectId!, period),
    enabled: Boolean(projectId) && summaryData?.data?.data?.enabled,
  });

  const { data: recentData } = useQuery({
    queryKey: ['analytics', 'recent', projectId],
    queryFn: () => apiHelpers.getAnalyticsRecentEvents(projectId!, 10),
    enabled: Boolean(projectId) && summaryData?.data?.data?.enabled,
    refetchInterval: 120000, // Refresh every 2 minutes
  });

  const summary = summaryData?.data?.data;
  const topPages = topPagesData?.data?.data || [];
  const sources = sourcesData?.data?.data || [];
  const devices = devicesData?.data?.data || [];
  const recentEvents = recentData?.data?.data || [];

  const getDeviceIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'mobile':
        return Smartphone;
      case 'tablet':
        return Tablet;
      default:
        return Monitor;
    }
  };

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Analytics not enabled - show install prompt
  if (!summary?.enabled) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to={`/projects/${projectId}`}
            className="hover:bg-muted rounded-lg p-2 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Analytics</h1>
            <p className="text-muted-foreground">Website traffic and performance metrics</p>
          </div>
        </div>

        <div className="bg-card rounded-xl border p-8 text-center">
          <BarChart3 className="text-muted-foreground mx-auto mb-4 h-16 w-16" />
          <h2 className="mb-2 text-xl font-semibold">Analytics Not Enabled</h2>
          <p className="text-muted-foreground mx-auto mb-6 max-w-md">
            {summary?.message ||
              'Install the Built-in Analytics plugin to start tracking your website visitors, page views, and more.'}
          </p>
          <Link
            to="/plugins"
            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-6 py-3 font-medium"
          >
            <TrendingUp className="h-4 w-4" />
            Install Analytics Plugin
          </Link>
        </div>
      </div>
    );
  }

  const stats = summary.stats;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={`/projects/${projectId}`}
            className="hover:bg-muted rounded-lg p-2 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Analytics</h1>
            <p className="text-muted-foreground">Real-time website traffic and metrics</p>
          </div>
        </div>

        {/* Period Selector */}
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="bg-background rounded-lg border px-3 py-2 text-sm"
        >
          {PERIOD_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="bg-card rounded-xl border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-foreground text-sm">Unique Visitors</p>
              <p className="mt-1 text-2xl font-bold">{(stats?.visitors || 0).toLocaleString()}</p>
              {(stats?.visitorsChange ?? 0) !== 0 && (
                <p
                  className={`mt-1 flex items-center gap-1 text-xs ${(stats?.visitorsChange ?? 0) > 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  {(stats?.visitorsChange ?? 0) > 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  {(stats?.visitorsChange ?? 0) > 0 ? '+' : ''}
                  {stats?.visitorsChange}% from previous period
                </p>
              )}
            </div>
            <div className="rounded-lg bg-blue-100 p-3 dark:bg-blue-900/30">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-foreground text-sm">Page Views</p>
              <p className="mt-1 text-2xl font-bold">{(stats?.pageViews || 0).toLocaleString()}</p>
              {(stats?.pageViewsChange ?? 0) !== 0 && (
                <p
                  className={`mt-1 flex items-center gap-1 text-xs ${(stats?.pageViewsChange ?? 0) > 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  {(stats?.pageViewsChange ?? 0) > 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  {(stats?.pageViewsChange ?? 0) > 0 ? '+' : ''}
                  {stats?.pageViewsChange}% from previous period
                </p>
              )}
            </div>
            <div className="rounded-lg bg-green-100 p-3 dark:bg-green-900/30">
              <Eye className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-foreground text-sm">Sessions</p>
              <p className="mt-1 text-2xl font-bold">{(stats?.sessions || 0).toLocaleString()}</p>
              <p className="text-muted-foreground mt-1 text-xs">
                {stats?.avgPagesPerSession || '0'} pages/session
              </p>
            </div>
            <div className="rounded-lg bg-purple-100 p-3 dark:bg-purple-900/30">
              <Activity className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-foreground text-sm">Custom Events</p>
              <p className="mt-1 text-2xl font-bold">{(stats?.events || 0).toLocaleString()}</p>
              <p className="text-muted-foreground mt-1 text-xs">Tracked interactions</p>
            </div>
            <div className="rounded-lg bg-orange-100 p-3 dark:bg-orange-900/30">
              <MousePointer className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Pages */}
        <div className="bg-card rounded-xl border p-6">
          <h3 className="mb-4 font-semibold">Top Pages</h3>
          {pagesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : topPages.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No page views recorded yet
            </p>
          ) : (
            <div className="space-y-4">
              {topPages.map((page) => (
                <div key={page.path}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="max-w-[200px] truncate text-sm font-medium" title={page.path}>
                      {page.path}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      {page.views.toLocaleString()} views
                    </span>
                  </div>
                  <div className="bg-muted h-2 overflow-hidden rounded-full">
                    <div
                      className="bg-primary h-full rounded-full transition-all"
                      style={{ width: `${page.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Traffic Sources */}
        <div className="bg-card rounded-xl border p-6">
          <h3 className="mb-4 font-semibold">Traffic Sources</h3>
          {sourcesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : sources.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">No traffic data yet</p>
          ) : (
            <div className="space-y-4">
              {sources.map((source) => (
                <div key={source.source} className="flex items-center gap-4">
                  <div className="bg-muted flex h-10 w-10 items-center justify-center rounded-lg">
                    <Globe className="text-muted-foreground h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-sm font-medium">{source.source}</span>
                      <span className="text-muted-foreground text-sm">{source.percentage}%</span>
                    </div>
                    <div className="bg-muted h-2 overflow-hidden rounded-full">
                      <div
                        className="h-full rounded-full bg-blue-500 transition-all"
                        style={{ width: `${source.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Devices */}
        <div className="bg-card rounded-xl border p-6">
          <h3 className="mb-4 font-semibold">Devices</h3>
          {devicesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : devices.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">No device data yet</p>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {devices.map((device) => {
                const Icon = getDeviceIcon(device.type);
                return (
                  <div key={device.type} className="bg-muted/50 rounded-lg p-4 text-center">
                    <Icon className="text-muted-foreground mx-auto mb-2 h-8 w-8" />
                    <p className="font-medium capitalize">{device.type}</p>
                    <p className="text-primary text-2xl font-bold">{device.percentage}%</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-card rounded-xl border p-6">
          <h3 className="mb-4 font-semibold">Recent Activity</h3>
          {recentEvents.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">No recent activity</p>
          ) : (
            <div className="max-h-[300px] space-y-3 overflow-y-auto">
              {recentEvents.map((event) => (
                <div key={event.id} className="flex items-center gap-3 text-sm">
                  <div
                    className={`h-2 w-2 rounded-full ${event.type === 'pageview' ? 'bg-green-500' : 'bg-blue-500'}`}
                  />
                  <span className="text-muted-foreground flex-shrink-0 capitalize">
                    {event.type === 'pageview' ? 'View' : event.name}
                  </span>
                  <span className="truncate font-medium" title={event.path}>
                    {event.path}
                  </span>
                  <span className="text-muted-foreground ml-auto flex-shrink-0 text-xs">
                    {new Date(event.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
